// Content.tsx (FULL VERSION)

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/Button';
import { ContentDisplay } from '../components/ContentDisplay';
import { supabase } from '../lib/supabase';
import { format } from 'date-fns';
import { ProtectedLayout } from '../components/ProtectedLayout';

export function Content() {
  const navigate = useNavigate();

  const [subscriptionTier, setSubscriptionTier] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [error, setError] = useState('');

  const [lessons, setLessons] = useState<any[]>([]);
  const [selectedLesson, setSelectedLesson] = useState('');
  const [customInput, setCustomInput] = useState('');
  const [isCustomInput, setIsCustomInput] = useState(false);

  const [voiceProfile, setVoiceProfile] = useState<any>(null);
  const [showVoiceProfile, setShowVoiceProfile] = useState(false);

  const [platforms, setPlatforms] = useState<any[]>([]);
  const [selectedPlatform, setSelectedPlatform] = useState('');
  const [cta, setCta] = useState('');
  const [customCTA, setCustomCTA] = useState('');
  const [callToActions, setCallToActions] = useState<any[]>([]);

  const [generatedTopics, setGeneratedTopics] = useState<string[]>([]);
  const [generatedQuotes, setGeneratedQuotes] = useState<string[]>([]);
  const [selectedIdea, setSelectedIdea] = useState('');
  const [selectedType, setSelectedType] = useState<'topic' | 'quote' | ''>('');

  const [generatedPosts, setGeneratedPosts] = useState<any[]>([]);
  const [showContent, setShowContent] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGeneratingTopics, setIsGeneratingTopics] = useState(false);

  const [coreValues, setCoreValues] = useState<string[]>([]);
  const [valueFocus, setValueFocus] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        if (userError || !user) {
          setError('User not authenticated.');
          return;
        }

        const { data: subscription } = await supabase
          .from('subscriptions')
          .select('tier_id, status')
          .eq('user_id', user.id)
          .eq('status', 'active')
          .order('created_at', { ascending: false })
          .limit(1)
          .single();

        let mappedTier = 'start';
        if (subscription?.tier_id) {
          const { data: tierData } = await supabase
            .from('subscription_tiers')
            .select('name')
            .eq('id', subscription.tier_id)
            .single();
          mappedTier = tierData?.name || 'start';
        }
        setSubscriptionTier(mappedTier);

        const [
          { data: lessonsData },
          { data: voiceData },
          { data: ctaData },
          { data: platformData },
          { data: valueData }
        ] = await Promise.all([
          supabase.from('lessons_learned').select('*').order('date', { ascending: false }),
          supabase.from('voice_profiles').select('*').eq('user_id', user.id).maybeSingle(),
          supabase.from('call_to_actions').select('*').eq('user_id', user.id).order('use_count', { ascending: false }),
          supabase.from('platforms').select('*').order('name'),
          supabase.from('profiles').select('core_value_1, core_value_2, core_value_3').eq('id', user.id).single()
        ]);

        setLessons(lessonsData || []);
        setVoiceProfile(voiceData);
        setCallToActions(ctaData || []);
        setPlatforms(platformData || []);
        setCoreValues([
          valueData?.core_value_1,
          valueData?.core_value_2,
          valueData?.core_value_3
        ].filter(Boolean));

        setHasAccess(true);
      } catch (err: any) {
        console.error('Data fetch failed:', err.message);
        setError('There was a problem loading your data.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const sanitizeForJSON = (text: string) =>
    text.replace(/[^\x20-\x7E]/g, ' ').replace(/\n/g, ' ').replace(/"/g, "'");

  const handleGenerateTopics = async () => {
    setIsGeneratingTopics(true);
    setError('');

    const rawLesson = lessons.find((l) => l.id === selectedLesson);
    const rawContent = isCustomInput ? customInput.trim() : rawLesson?.lesson?.trim() || '';

    if (!rawContent) {
      setError('Your content is empty. Please write something or choose a valid lesson.');
      setIsGeneratingTopics(false);
      return;
    }

    const content = sanitizeForJSON(rawContent);

    try {
      const token = (await supabase.auth.getSession()).data.session?.access_token;
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_EDGE_URL}/generate-topics`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ content, isCustomInput }),
      });

      const result = await response.json();
      setGeneratedTopics(result.topics || []);
      setGeneratedQuotes(result.quotes || []);
    } catch (err: any) {
      console.error('Failed to generate topics:', err.message);
      setError('There was a problem generating topics. Please try again.');
    } finally {
      setIsGeneratingTopics(false);
    }
  };

  const handleGenerateContent = async () => {
    const missingFields: string[] = [];
    if (!selectedPlatform) missingFields.push('Platform');
    if (!cta) missingFields.push('Call to Action');
    if (!selectedLesson && !isCustomInput) missingFields.push('Lesson');
    if (!selectedIdea) missingFields.push('Topic or Quote');
    if (!valueFocus) missingFields.push('Core Value');

    if (missingFields.length > 0) {
      setError(`Please complete the following fields before generating content: ${missingFields.join(', ')}`);
      return;
    }

    setIsGenerating(true);
    const idea = selectedIdea;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const { data: { user } } = await supabase.auth.getUser();

      await supabase.from('content_choices').insert({
        user_id: user.id,
        lesson_id: selectedLesson,
        choice_text: selectedIdea,
        choice_type: selectedType
      });

      const content = isCustomInput
        ? customInput.trim()
        : lessons.find((l) => l.id === selectedLesson)?.lesson?.trim();

      const platformData = platforms.find((p) => p.name === selectedPlatform);
      const formatInstructions = platformData?.format_instructions || '';

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_EDGE_URL}/generate-content`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          platform: selectedPlatform,
          idea,
          type: selectedType,
          content,
          cta: cta === 'new' ? customCTA : cta,
          voiceProfile,
          lesson_id: selectedLesson,
          value_focus: valueFocus,
          format_instructions: formatInstructions
        })
      });

      const result = await response.json();
      setGeneratedPosts(result.posts || []);
      setShowContent(true);
    } catch (err: any) {
      console.error('Error generating content:', err.message);
      setError('There was a problem generating content. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleGenerateVoiceProfile = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const { data: { user } } = await supabase.auth.getUser();
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_EDGE_URL}/generate-voice-profile`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ user_id: user.id })
      });
      const result = await response.json();
      setVoiceProfile(result);
    } catch (err: any) {
      console.error('Error generating voice profile:', err.message);
      setError('There was a problem generating your voice profile. Please try again.');
    }
  };

  return (
      <ProtectedLayout>
    <div className="p-6 max-w-5xl mx-auto space-y-8">
      {error && <div className="text-red-600">{error}</div>}

      {voiceProfile && (
        <div className="bg-white p-4 rounded-xl shadow space-y-2">
          <div className="flex justify-between items-center">
            <h1 className="text-xl font-bold flex items-center gap-2">
              <button onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="w-5 h-5 text-[#236caf]" />
              </button>
              Create Content
            </h1>
            <div className="flex gap-2 items-center">
              <button onClick={() => setShowVoiceProfile(!showVoiceProfile)}>
                {showVoiceProfile ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
              <Button variant="outline" size="sm" onClick={handleGenerateVoiceProfile}>
                Generate Voice Profile
              </Button>
            </div>
          </div>
          {showVoiceProfile && (
            <div className="text-sm space-y-1">
              <p><strong>Tone Style:</strong> {voiceProfile.tone_style}</p>
              <p><strong>Story Patterns:</strong> {voiceProfile.story_patterns}</p>
              <p><strong>Common Language:</strong> {voiceProfile.common_language}</p>
              <p><strong>Themes:</strong> {voiceProfile.themes}</p>
              <p><strong>Post Structure:</strong> {voiceProfile.post_structure}</p>
            </div>
          )}
        </div>
      )}

      <div className="bg-white rounded-xl p-6 shadow space-y-6">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Platform</label>
            <select value={selectedPlatform} onChange={(e) => setSelectedPlatform(e.target.value)} className="w-full border rounded p-2">
              <option value="" disabled>Select a platform</option>
              {platforms.map((p, i) => <option key={i} value={p.name}>{p.name}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Call to Action</label>
            <select value={cta} onChange={(e) => setCta(e.target.value)} className="w-full border rounded p-2">
              <option value="" disabled>Select a call to action</option>
              {callToActions.map((c, i) => <option key={i} value={c.text}>{c.text}</option>)}
              <option value="new">Add New CTA</option>
            </select>
            {cta === 'new' && (
              <input value={customCTA} onChange={(e) => setCustomCTA(e.target.value)} className="mt-2 w-full border rounded p-2" placeholder="Enter new CTA" />
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Lesson</label>
            <select value={selectedLesson} onChange={(e) => setSelectedLesson(e.target.value)} className="w-full border rounded p-2">
              <option value="" disabled>Select a lesson</option>
              {lessons.map((l, i) => (
                <option key={i} value={l.id}>{format(new Date(l.date), 'MMM dd yyyy')} - {l.lesson.substring(0, 60)}...</option>
              ))}
            </select>
            <div className="flex items-center mt-2 gap-2">
              <input type="checkbox" checked={isCustomInput} onChange={() => setIsCustomInput(!isCustomInput)} />
              <label className="text-sm text-gray-600">Use custom input</label>
            </div>
            {isCustomInput && (
              <textarea value={customInput} onChange={(e) => setCustomInput(e.target.value)} className="w-full mt-2 border rounded p-2" rows={4} placeholder="Enter your own reflection..." />
            )}
          </div>

          <Button
            onClick={handleGenerateTopics}
            disabled={
              isGeneratingTopics ||
              !selectedPlatform ||
              !cta ||
              (!isCustomInput && !selectedLesson && customInput.trim() === '')
            }
            className="w-full"
          >
            {isGeneratingTopics ? 'Generating...' : 'Generate Topic & Quote Ideas'}
          </Button>

          {(generatedTopics.length > 0 || generatedQuotes.length > 0) && (
            <div className="text-center">
              <h3 className="font-medium mb-4 text-xl">Choose a topic or a quote</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto text-left">
                <div>
                  <h4 className="font-semibold text-center mb-2">Topics</h4>
                  {generatedTopics.map((t, i) => (
                    <div
                      key={i}
                      className={`p-2 border rounded my-1 cursor-pointer ${selectedIdea === t && selectedType === 'topic' ? 'bg-blue-50' : ''}`}
                      onClick={() => {
                        setSelectedIdea(t);
                        setSelectedType('topic');
                      }}
                    >
                      {t}
                    </div>
                  ))}
                </div>
                <div>
                  <h4 className="font-semibold text-center mb-2">Quotes</h4>
                  {generatedQuotes.map((q, i) => (
                    <div
                      key={i}
                      className={`p-2 border rounded my-1 cursor-pointer ${selectedIdea === q && selectedType === 'quote' ? 'bg-blue-50' : ''}`}
                      onClick={() => {
                        setSelectedIdea(q);
                        setSelectedType('quote');
                      }}
                    >
                      "{q}"
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {selectedIdea && (
            <div className="mt-4">
              <h3 className="font-medium mb-4 text-xl text-center">
                What value would you like the reader to experience?
              </h3>
              <select
  value={valueFocus}
  onChange={(e) => setValueFocus(e.target.value)}
  className="w-full border rounded p-2 text-center" 
>

                <option value="" disabled>
                  Select a core value
                </option>
                {coreValues.map((val, i) => (
                  <option key={i} value={val}>
                    {val}
                  </option>
                ))}
              </select>
            </div>
          )}

          <Button
            onClick={handleGenerateContent}
            disabled={isGenerating || !selectedIdea || !valueFocus}
            className="w-full"
          >
            {isGenerating ? 'Generating Content...' : 'Generate Content'}
          </Button>

          {showContent && generatedPosts.length > 0 && (
            <ContentDisplay posts={generatedPosts} onClose={() => setShowContent(false)} />
          )}
        </div>
      </div>
    </div>
      </ProtectedLayout>
  );
}
